import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/lib/useSeo";
import {
  AuthShell,
  PrimaryButton,
  TextField,
  ErrorBox,
} from "@/components/AuthKit";

/**
 * ForgotPassword — 1:1 port of iOS `src/app/(auth)/forgot.tsx`.
 * Enter email → request reset code → navigate to /reset with email prefilled.
 */
export default function ForgotPassword() {
  const navigate = useNavigate();
  const { forgotPassword, apiError } = useAuth();

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (loading) return;
    setErr("");
    if (!email.trim()) return setErr("Email is required.");
    setLoading(true);
    try {
      await forgotPassword(email.trim());
      toast.success("If that email exists, we sent a reset code.");
      navigate(`/reset?email=${encodeURIComponent(email.trim())}`);
    } catch (e2) {
      setErr(apiError(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      testId="forgot-screen"
      title="Reset your password"
      subtitle="Enter your email and we'll send you a reset code."
    >
      <Seo
        title="Forgot password — BrontoY"
        description="Reset your BrontoY password. Enter the email associated with your account and we will send you a 6-digit reset code."
        canonicalPath="/forgot"
        noindex
      />
      <form onSubmit={submit} className="flex flex-col gap-4 mt-8">
        <TextField
          id="forgot-email"
          label="EMAIL"
          type="email"
          autoComplete="email"
          autoCapitalize="none"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          testId="forgot-email-input"
        />

        {err ? <ErrorBox testId="forgot-error">{err}</ErrorBox> : null}

        <PrimaryButton
          type="submit"
          label="Send reset code"
          loading={loading}
          className="mt-1"
          testId="forgot-submit-btn"
        />
      </form>

      <div className="flex items-center justify-center mt-7 text-[14px]">
        <span className="text-muted-foreground">Remembered it?&nbsp;</span>
        <button
          type="button"
          onClick={() => navigate("/login", { replace: true })}
          className="text-foreground font-bold hover:opacity-80"
          data-testid="forgot-to-login"
        >
          Log in
        </button>
      </div>
    </AuthShell>
  );
}
